/*
 * 水印预览组件 - 在手机图片上层显示水印内容预览
 */
import React, { useState, useEffect } from 'react'
import { message } from 'antd'
import { useAppSelector } from '../../../../../app/hooks'
import { WATERMARK_CONFIG } from '../../../../../constants/watermark'
import phoneImg from '../../../../../images/watermark/phone.png'
import './index.scss'

// 不支持预览的项（即使开启了也不显示）
const UNSUPPORTED_PREVIEW_TITLES = [
  '拍照要求',
  '个人名片',
  '地图',
  '计数',
  '竣工倒计时',
  '整改二维码'
]

// 放在底部独立展示的项（背景透明）
const BOTTOM_ITEMS = [
  '建设单位',
  '监理单位',
  '施工单位',
  '设计单位',
  '勘察单位'
]

// 星期几的中文表示
const WEEKDAYS = [
  '星期日',
  '星期一',
  '星期二',
  '星期三',
  '星期四',
  '星期五',
  '星期六'
]

interface PreviewItem {
  title: string
  content: string
  example: string
  key: string
}

interface WatermarkPreviewProps {
  groupName?: string
  clickEvent?: (item: string) => void
}

// 格式化当前时间
const formatCurrentTime = (style: number): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  const weekday = WEEKDAYS[now.getDay()]

  const dateStr = `${year}.${month}.${day}`

  switch (style) {
    case 100: // 日期+时刻
      return `${dateStr} ${hours}:${minutes}`
    case 101: // 日期+星期
      return `${dateStr} ${weekday}`
    case 102: // 星期+时刻
      return `${weekday} ${hours}:${minutes}`
    case 103: // 日期
      return dateStr
    case 104: // 日期+星期+时刻
      return `${dateStr} ${weekday} ${hours}:${minutes}`
    case 105: // 星期
      return weekday
    case 106: // 时刻
      return `${hours}:${minutes}`
    case 107:
    case 108: // 日期+时分秒
      return `${dateStr} ${hours}:${minutes}:${seconds}`
    default:
      return `${dateStr} ${hours}:${minutes}`
  }
}

// 转换十六进制颜色为 RGB
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : { r: 0, g: 0, b: 0 }
}

// 根据 alpha 生成背景样式
const getBackgroundStyle = (themeColor: string, alpha: string) => {
  const rgb = hexToRgb(themeColor)
  const alphaNum = parseFloat(alpha)

  // 当 alpha 为 0 时，无遮挡形式，50% 不透明度
  if (alphaNum === 0) {
    return {
      background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5)`,
      isFullWidth: true,
      isItemTransparent: false
    }
  }

  // 当 alpha >= 0.5 时，使用普通半透明背景
  if (alphaNum >= 0.5) {
    return {
      background: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alphaNum})`,
      isFullWidth: false,
      isItemTransparent: false
    }
  }

  // 当 alpha < 0.5 时，使用从左到右的渐变（透明度从 0.5 到 alpha）
  return {
    background: `linear-gradient(to right, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5), rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alphaNum}))`,
    isFullWidth: false,
    isItemTransparent: true
  }
}

const WatermarkPreview: React.FC<WatermarkPreviewProps> = (props) => {
  const watermark = useAppSelector((state) => state.editor.watermark)
  const baseId = watermark?.base_id
  const items = watermark?.items || []
  const theme = watermark?.theme

  const [titleContainerEl, setTitleContainerEl] = useState<HTMLDivElement | null>(null)
  const [isTitleTall, setIsTitleTall] = useState(false)

  useEffect(() => {
    if (!titleContainerEl) return
    const observer = new ResizeObserver(([entry]) => {
      setIsTitleTall(entry.contentRect.height > 56)
    })
    observer.observe(titleContainerEl)
    return () => observer.disconnect()
  }, [titleContainerEl])

  // 如果没有选择水印类型，不显示预览
  if (!baseId || !WATERMARK_CONFIG[baseId]) {
    return (
      <div className="watermark-preview">
        <img src={phoneImg} alt="手机预览" className="phone-bg" />
        <div className="watermark-content">
          <div className="preview-placeholder">请选择水印类型</div>
        </div>
      </div>
    )
  }

  // 获取主题样式
  const themeColor =
    theme?.color || (WATERMARK_CONFIG[baseId]?.theme as any)?.color || '#ffc233'
  const textColor = '#ffffff'
  const alpha = theme?.alpha || '0.5'
  const backgroundStyle = getBackgroundStyle(themeColor, alpha)
  const sizeScale =
    alpha !== '0' ? parseFloat(theme?.sizeScale || '1.0') : 1.0

  // 收集所有开启的项，分离普通项和底部项
  const getEnabledItems = (): { normalItems: PreviewItem[]; bottomItems: PreviewItem[] } => {
    if (!items || items.length === 0) return { normalItems: [], bottomItems: [] }

    const normalItems: PreviewItem[] = []
    const bottomItems: PreviewItem[] = []
    items.forEach((item: any, index: number) => {
      const itemId = item?.id
      const itemConfig = WATERMARK_CONFIG[baseId]?.[itemId]
      const switchStatus = item?.switchStatus
      const content = item?.content || ''
      const title = item?.title || ''
      const style = item?.style

      // 只收集开启的项
      if (!switchStatus) return

      // 过滤掉不支持预览的项
      if (UNSUPPORTED_PREVIEW_TITLES.includes(title)) return

      // 获取选项样式配置
      let displayContent = content
      let example = ''
      if (itemConfig?.styles && style !== undefined) {
        const styleConfig = (itemConfig.styles as any[]).find((s) => s.id === style)
        if (styleConfig) {
          displayContent = styleConfig.content || content
          example = styleConfig.example || ''
        }
      }

      // 拍摄时间条目使用当前时间
      if (title === '拍摄时间' && style !== undefined) {
        displayContent = formatCurrentTime(style)
      }

      const itemData: PreviewItem = {
        title,
        content: displayContent,
        example,
        key: `${itemId}-${index}`
      }

      // 判断是否是底部项
      if (BOTTOM_ITEMS.includes(title)) {
        bottomItems.push(itemData)
      } else {
        normalItems.push(itemData)
      }
    })
    return { normalItems, bottomItems }
  }

  const { normalItems, bottomItems } = getEnabledItems()

  // 获取工程名称
  const projectInfo = items?.find?.((item: any) => item.title === '工程名称')

  // 渲染普通条目（每行2个）
  const renderNormalItems = () => {
    console.log('normalItems', normalItems)
    const normalItemsWithoutProjectName = normalItems.filter(
      (item) => item.title !== '工程名称'
    )
    // 当 alpha 为 0 时，每行显示 2 个条目
    if (backgroundStyle.isFullWidth) {
      const rows: React.ReactElement[] = []
      for (let i = 0; i < normalItemsWithoutProjectName.length; i += 2) {
        const rowItems = normalItemsWithoutProjectName.slice(i, i + 2)
        rows.push(
          <div key={i} className="watermark-item-row">
            {rowItems.map((item) => (
              <div key={item.key} className="watermark-item">
                {item.title && (
                  <span className="item-title">
                    <span className="item-title-text">{item.title}</span>:{' '}
                  </span>
                )}
                <span
                  className={`item-content${!item.content ? ' empty' : ''}`}
                >
                  {item?.example || item.content || '请输入文案或选择条目'}
                </span>
              </div>
            ))}
          </div>
        )
      }
      return rows
    }

    // 默认每行显示 1 个条目
    return normalItemsWithoutProjectName.map((item) => (
      <div key={item.key} className="watermark-item">
        {item.title && (
          <span className="item-title">
            <span className="item-title-text">{item.title}</span>:{' '}
          </span>
        )}
        {!item.content ? (
          <span
            className="empty"
            style={{
              color: backgroundStyle.isItemTransparent
                ? '#ffffff80'
                : 'rgba(0, 0, 0, 0.35)'
            }}
          >
            {'请输入文案或选择条目'}
          </span>
        ) : (
          <span className="item-content">{item?.example || item.content}</span>
        )}
      </div>
    ))
  }

  // 渲染水印项 - 所有条目放在一个框中
  const renderItems = () => {
    if (normalItems.length === 0 && bottomItems.length === 0) return null

    // 当 alpha 为 0 时的特殊样式
    const boxStyle = backgroundStyle.isFullWidth
      ? {
          color: textColor,
          width: '300px',
          left: 0,
          bottom: '196px'
        }
      : {
          color: textColor
        }

    // 外层容器样式，应用 sizeScale 缩放
    const containerStyle = {
      transform: `scale(${sizeScale})`,
      transformOrigin: 'left bottom' as const
    }

    return (
      <div
        className={`watermark-box-container${
          backgroundStyle.isFullWidth ? '-full-width' : ''
        }`}
        style={containerStyle}
      >
        <div className="watermark-box" style={boxStyle}>
          <div
            ref={setTitleContainerEl}
            className="watermark-title-container"
            style={{ background: backgroundStyle.background }}
          >
            {renderLogo()}
            <div className="watermark-title">
              <div className="watermark-text">
                {projectInfo?.switchStatus
                  ? projectInfo?.content || '工程记录'
                  : '工程记录'}
              </div>
            </div>
          </div>
          <div
            className="normal-items-container"
            style={{
              background: backgroundStyle.isItemTransparent
                ? 'transparent'
                : '#DEDEDE',
              color: backgroundStyle.isItemTransparent ? '#ffffff' : '#000',
              borderRadius:
                bottomItems?.length === 0 && !backgroundStyle.isFullWidth
                  ? '0 0 4px 4px'
                  : '0 0 0 0'
            }}
          >
            {renderNormalItems()}
          </div>
          {bottomItems.map((item) => (
            <div
              key={item.key}
              className="watermark-bottom-item"
              style={{
                background: backgroundStyle.background,
                color: textColor
              }}
            >
              {item.title && (
                <span className="item-title">
                  <span className="item-title-text">{item.title}</span>:{' '}
                </span>
              )}
              <span className={`item-content${!item.content ? ' empty' : ''}`}>
                {item.content || '请输入文案或选择条目'}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const handleClick = () => {
    message.info('此处只支持预览')
  }

  // 渲染 logo
  const renderLogo = () => {
    const logo = watermark?.logo
    const switchStatus = logo?.switchStatus
    const url = logo?.url
    const scale = logo?.scale || 1

    if (!url || !switchStatus) {
      return (
        <div className="watermark-logo-empty">
          <div className="empty-content"></div>
        </div>
      )
    }
    const logoContentStyle = isTitleTall
      ? { borderRadius: '4px', marginLeft: '4px' }
      : {}

    return (
      <div className="watermark-logo">
        <div className="watermark-logo-content" style={logoContentStyle}>
          <img
            src={url}
            alt="logo"
            style={{
              transform: `scale(${scale})`
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="watermark-preview">
      <img src={phoneImg} alt="手机预览" className="phone-bg" />
      <div className="watermark-content" onClick={handleClick}>
        {renderItems()}
      </div>
      <div className="group-name">同步： {props?.groupName}</div>
    </div>
  )
}

export default WatermarkPreview
